'use client'

import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { clinicasService } from '../../services/clinicas.service'
import type { ClinicaItem } from '../../services/clinicas.service'
import { DomainService } from '../../services/domain.service'
import type { ContractType, Formacao } from '../../services/domain.service'
import { variacoesService } from '../../services/variacoes.service'
import { cargosService, type Cargo } from '../../services/cargos.service'
import { setoresService, type Setor } from '../../services/setores.service'
import { colaboradoresService, type Colaborador } from '../../services/colaboradores.service'
import SearchLookupModal from '../Modals/SearchLookupModal'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import ClinicaSearch from './ClinicaSearch'
import CollaboratorSearch from './CollaboratorSearch'
import { formatCPF, formatTelefone } from '../../utils/formatters'
import { maskPhone } from '../../utils/masks'
import { useEmpresa } from '../../contexts/EmpresaContext'

interface Employee {
  id: string
  nome: string
  nome_social?: string
  pcd?: boolean
  enviar_esocial?: boolean
  regime_contratacao?: string
  estado_civil?: string
  cpf: string
  nascimento?: string
  sexo?: string
  cargo?: string
  setor?: string
  email?: string
  telefone?: string
  formacao?: string
  dataAdmissao?: string
  demissao?: string
  exame?: string
  variacao?: string
  clinica?: string
  clinicaNome?: string
  dataAgendamento?: string
  examesSelecionados?: number[]
  examesSelecionadosCount?: number
  modeloExame?: string
  tipoContrato?: string
  is_candidato?: boolean
  escolha_clinica_tipo?: 'rh' | 'colaborador'
  matricula_esocial?: string
  cbo?: string
  cargo_id?: number
  setor_id?: number
  is_existente?: boolean
}

interface EmployeeFormProps {
  onAddEmployee: (employee: Employee) => void
  editingEmployee?: Employee | null
  onCancelEdit?: () => void
  defaultOpen?: boolean
  hasEmployees?: boolean // Indica se já existem colaboradores cadastrados
  hideAccordion?: boolean // Quando true, renderiza o formulário diretamente aberto sem barra verde
  defaultIsCandidato?: boolean
  defaultEscolhaClinicaTipo?: 'rh' | 'colaborador'
}

export default function EmployeeForm({
  onAddEmployee,
  editingEmployee,
  onCancelEdit,
  defaultOpen = false,
  hasEmployees = false,
  hideAccordion = false,
  defaultIsCandidato = false,
  defaultEscolhaClinicaTipo,
}: EmployeeFormProps) {
  const { empresa } = useEmpresa()
  const [isOpen, setIsOpen] = useState(hideAccordion ? true : defaultOpen)
  const wasOpenRef = useRef(defaultOpen) // Ref para rastrear estado anterior sem causar re-renders
  const accordionHeaderRef = useRef<HTMLButtonElement>(null)
  const [selectedClinica, setSelectedClinica] = useState<ClinicaItem | null>(null)
  const [editingClinica, setEditingClinica] = useState(false) // Controla se está editando a clínica
  const [isCandidato, setIsCandidato] = useState(
    editingEmployee?.is_candidato ?? defaultIsCandidato ?? false
  )
  const [escolhaClinicaTipo, setEscolhaClinicaTipo] = useState<'rh' | 'colaborador'>(
    editingEmployee?.escolha_clinica_tipo ??
    defaultEscolhaClinicaTipo ??
    (defaultIsCandidato ? 'colaborador' : 'rh')
  )
  const [cargosList, setCargosList] = useState<Cargo[]>([])
  const [setoresList, setSetoresList] = useState<Setor[]>([])
  const [isCargoModalOpen, setIsCargoModalOpen] = useState(false)
  const [isSetorModalOpen, setIsSetorModalOpen] = useState(false)
  const [showCargoSuggestions, setShowCargoSuggestions] = useState(false)
  const [showSetorSuggestions, setShowSetorSuggestions] = useState(false)
  const [matchedCbo, setMatchedCbo] = useState<string | null>(null)
  const [colaboradorExistenteDetectado, setColaboradorExistenteDetectado] = useState<Colaborador | null>(null)
  const [existingColaboradorId, setExistingColaboradorId] = useState<number | null>(null)
  const [loadingExamesCargo, setLoadingExamesCargo] = useState(false)
  const [cargoExamesAviso, setCargoExamesAviso] = useState<string | null>(null)
  const [loadingCargosDoSetor, setLoadingCargosDoSetor] = useState(false)
  const loadedSetorIdRef = useRef<number | null>(null)
  const [contractTypes, setContractTypes] = useState<ContractType[]>([])
  const [formacoes, setFormacoes] = useState<Formacao[]>([])
  const [tiposExame, setTiposExame] = useState<Array<{ id: number; nome: string }>>([])
  const [variacaoSelecionada, setVariacaoSelecionada] = useState<number | null>(null)
  const [examesSelecionados, setExamesSelecionados] = useState<number[]>([])
  const [datasDisponiveis, setDatasDisponiveis] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    nome: '',
    nome_social: '',
    pcd: false,
    enviar_esocial: true,
    regime_contratacao: '',
    estado_civil: '',
    cpf: '',
    nascimento: '2000-01-01',
    sexo: '',
    cargo: '',
    setor: '',
    email: '',
    telefone: '',
    formacao: '',
    dataAdmissao: '',
    demissao: '',
    matricula_esocial: '',
    exame: '', // DEVE ser selecionado pelo usuário, sem padrão
    variacao: '1', // Nenhuma atividade complementar (padrão)
    clinica: '',
    dataAgendamento: '',
    modeloExame: '0', // Presencial
    tipoContrato: ''
  })



  // Buscar ID do tipo "Admissional" dinamicamente da API
  const admissionalId = useMemo(() => {
    const admissional = tiposExame.find(tipo =>
      tipo.nome.toLowerCase().includes('admissional')
    );
    return admissional ? admissional.id.toString() : '1';
  }, [tiposExame]);

  // Buscar ID do tipo "Periódico" dinamicamente da API
  const periodicoId = useMemo(() => {
    const periodico = tiposExame.find(tipo =>
      tipo.nome.toLowerCase().includes('periódico')
    );
    return periodico ? periodico.id.toString() : '3';
  }, [tiposExame]);

  // Garantir que novos cadastros sempre iniciem com o tipo Admissional
  useEffect(() => {
    if (admissionalId && !editingEmployee) {
      setFormData(prev => {
        if (!prev.exame || prev.exame === '') {
          return { ...prev, exame: admissionalId };
        }
        return prev;
      });
    }
  }, [admissionalId, editingEmployee]);

  // Carregar tipos de contrato, formações e variações
  useEffect(() => {
    const fetchDomainData = async () => {
      try {
        const [types, formations, examTypes, setoresRes] = await Promise.all([
          DomainService.getContractTypes(),
          DomainService.getFormacoes(),
          DomainService.getExamTypes(),
          setoresService.findAll({ empresa_id: empresa?.id, limit: 1000 }).catch(() => ({ data: [], meta: { total: 0, page: 1, limit: 1000, totalPages: 0 } })),
        ])

        setContractTypes(types)
        setFormacoes(formations)
        setTiposExame(examTypes)
        if (setoresRes?.data) {
          setSetoresList(setoresRes.data)
        }
      } catch (error) {
        console.error('Erro ao carregar dados de domínio:', error)
        // Fallback se falhar
        setContractTypes([
          { id: 1, valor: 'CLT', descricao: 'CLT' },
          { id: 2, valor: 'PJ', descricao: 'PJ' },
          { id: 3, valor: 'Estágio', descricao: 'Estágio' },
          { id: 4, valor: 'Jovem Aprendiz', descricao: 'Jovem Aprendiz' }
        ])
        setFormacoes([
          { id: 1, valor: 'ensino_fundamental', descricao: 'Ensino Fundamental' },
          { id: 2, valor: 'ensino_medio', descricao: 'Ensino Médio' },
          { id: 3, valor: 'ensino_superior', descricao: 'Ensino Superior' },
          { id: 4, valor: 'pos_graduacao', descricao: 'Pós-Graduação' }
        ])
      }
    }
    fetchDomainData()
  }, [empresa?.id])

  // Removido: lógica hardcoded de Santana para exames online
  // Agora o ClinicaSearch filtra automaticamente clínicas com horários online quando modeloExame === '1'

  // Carregar datas disponíveis quando clínica ou modeloExame for selecionado
  useEffect(() => {
    const fetchDatasDisponiveis = async () => {
      if (selectedClinica) {
        try {
          const isOnline = formData.modeloExame === '1'
          const datas = await clinicasService.getProximasDatasDisponiveis(selectedClinica.id, 60, isOnline)
          setDatasDisponiveis(datas)
        } catch (error) {
          console.error('Erro ao carregar datas disponíveis:', error)
          setDatasDisponiveis([])
        }
      } else {
        setDatasDisponiveis([])
      }
    }
    fetchDatasDisponiveis()
  }, [selectedClinica, formData.modeloExame])

  // Carregar dados salvos do localStorage ao montar
  useEffect(() => {
    const savedData = localStorage.getItem('employeeFormData')
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData)
        setFormData(prev => ({ ...prev, ...parsedData }))
      } catch (error) {
        console.error('Erro ao carregar dados salvos do formulário:', error)
      }
    }
  }, [])

  // Abrir automaticamente se defaultOpen for true e não estiver editando, ou se hideAccordion for true
  useEffect(() => {
    if (hideAccordion || (defaultOpen && !editingEmployee)) {
      setIsOpen(true)
    }
  }, [defaultOpen, editingEmployee, hideAccordion])

  // Detectar quando accordion é fechado no mobile e fazer scroll
  useEffect(() => {
    // Só fazer scroll se estava aberto e agora está fechado (fechou)
    const previousWasOpen = wasOpenRef.current
    if (previousWasOpen && !isOpen) {
      // Verificar se está no mobile (largura < 768px)
      const isMobile = window.innerWidth < 768

      if (isMobile) {
        // Pequeno delay para garantir que a animação do accordion terminou
        const timeoutId = setTimeout(() => {
          const headerOffset = 100 // Offset para header fixo (sticky top-0)

          if (hasEmployees) {
            // Se há colaboradores, fazer scroll para a lista de colaboradores
            const colaboradoresList = document.getElementById('colaboradores-cadastrados')
            if (colaboradoresList) {
              const elementPosition = colaboradoresList.getBoundingClientRect().top
              const offsetPosition = elementPosition + window.pageYOffset - headerOffset

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              })
            }
          } else {
            // Se não há colaboradores, fazer scroll para o cabeçalho do accordion
            // Com offset para não ficar embaixo do header fixo
            if (accordionHeaderRef.current) {
              const elementPosition = accordionHeaderRef.current.getBoundingClientRect().top
              const offsetPosition = elementPosition + window.pageYOffset - headerOffset

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              })
            }
          }
        }, 300) // Delay para aguardar animação do accordion

        return () => clearTimeout(timeoutId)
      }
    }

    // Atualizar ref com estado atual
    wasOpenRef.current = isOpen
  }, [isOpen, hasEmployees])

  // Carregar dados do colaborador sendo editado
  useEffect(() => {
    const loadEditingData = async () => {
      if (editingEmployee) {
        console.log('🔄 Carregando dados para edição:', editingEmployee.id, 'exames:', editingEmployee.examesSelecionados)
        console.log('📦 Dados completos do colaborador sendo editado:', JSON.stringify(editingEmployee, null, 2))
        // Mapear TODOS os campos do editingEmployee para formData
        // IMPORTANTE: Garantir que todos os campos estejam mapeados corretamente
        // Converter dataAgendamento de YYYY-MM-DD para DD/MM/YYYY se necessário
        let dataAgendamentoFormatada = editingEmployee.dataAgendamento || ''
        if (dataAgendamentoFormatada && dataAgendamentoFormatada.includes('-') && !dataAgendamentoFormatada.includes('/')) {
          // Converter de YYYY-MM-DD para DD/MM/YYYY
          const [ano, mes, dia] = dataAgendamentoFormatada.split('T')[0].split('-')
          dataAgendamentoFormatada = `${dia}/${mes}/${ano}`
          console.log('📅 Data de agendamento convertida:', editingEmployee.dataAgendamento, '->', dataAgendamentoFormatada)
        }

        const formDataMapeado = {
          nome: editingEmployee.nome || '',
          nome_social: editingEmployee.nome_social || '',
          pcd: editingEmployee.pcd ?? false,
          enviar_esocial: editingEmployee.enviar_esocial ?? true,
          regime_contratacao: editingEmployee.regime_contratacao || '',
          estado_civil: editingEmployee.estado_civil || '',
          cpf: formatCPF(editingEmployee.cpf || ''),
          nascimento: editingEmployee.nascimento || '2000-01-01',
          sexo: editingEmployee.sexo || '',
          cargo: editingEmployee.cargo || '',
          setor: editingEmployee.setor || '',
          email: editingEmployee.email || '',
          telefone: formatTelefone(editingEmployee.telefone || ''),
          formacao: editingEmployee.formacao || '',
          dataAdmissao: editingEmployee.dataAdmissao || '',
          demissao: editingEmployee.demissao || '',
          matricula_esocial: editingEmployee.matricula_esocial || '',
          exame: editingEmployee.exame || '',
          variacao: editingEmployee.variacao || '1',
          clinica: editingEmployee.clinica || '',
          dataAgendamento: dataAgendamentoFormatada,
          modeloExame: editingEmployee.modeloExame || '0',
          tipoContrato: editingEmployee.tipoContrato || ''
        }

        console.log('📋 Mapeamento completo de campos na edição:', {
          dataAgendamentoOriginal: editingEmployee.dataAgendamento,
          dataAgendamentoFormatada: formDataMapeado.dataAgendamento,
          tipoContrato: formDataMapeado.tipoContrato,
          modeloExame: formDataMapeado.modeloExame,
          variacao: formDataMapeado.variacao,
          clinica: formDataMapeado.clinica
        })

        setFormData(formDataMapeado)
        setIsCandidato(editingEmployee.is_candidato || false)
        setEscolhaClinicaTipo(editingEmployee.escolha_clinica_tipo || 'rh')

        // Configurar variação selecionada se existir
        if (editingEmployee.variacao && editingEmployee.variacao !== '1') {
          setVariacaoSelecionada(parseInt(editingEmployee.variacao))
        }

        // Carregar dados da clínica e exames
        if (editingEmployee.clinica) {
          try {
            const clinicaId = parseInt(editingEmployee.clinica)
            const variacaoId = editingEmployee.variacao && editingEmployee.variacao !== '1'
              ? parseInt(editingEmployee.variacao)
              : null

            // 1. Buscar dados da clínica
            const clinica = await clinicasService.getClinicaById(clinicaId)
            setSelectedClinica(clinica)
            setEditingClinica(false) // Iniciar com clínica não editando (mostrar card)

            // Carregar datas disponíveis para a clínica ao editar
            try {
              const isOnline = editingEmployee.modeloExame === '1'
              const datas = await clinicasService.getProximasDatasDisponiveis(clinicaId, 60, isOnline)
              setDatasDisponiveis(datas)
              console.log('📅 Datas disponíveis carregadas ao editar:', datas.length, 'datas')

              // Se temos dataAgendamento mas não está nas datas disponíveis, adicionar
              if (editingEmployee.dataAgendamento) {
                // Converter de YYYY-MM-DD para DD/MM/YYYY se necessário
                let dataFormatada = editingEmployee.dataAgendamento.split('T')[0]
                if (dataFormatada.includes('-') && !dataFormatada.includes('/')) {
                  const [ano, mes, dia] = dataFormatada.split('-')
                  dataFormatada = `${dia}/${mes}/${ano}`
                }

                // Se a data não está na lista, adicionar
                if (!datas.includes(dataFormatada)) {
                  setDatasDisponiveis([...datas, dataFormatada])
                  console.log('📅 Data de agendamento adicionada às datas disponíveis:', dataFormatada)
                }
              }
            } catch (error) {
              console.error('❌ Erro ao carregar datas disponíveis ao editar:', error)
            }

            // 2. Separar exames obrigatórios (da API) dos opcionais (selecionados manualmente)
            // ExamesList vai buscar os exames da API internamente
            if (editingEmployee.examesSelecionados && editingEmployee.examesSelecionados.length > 0) {
              // Buscar Exame Clínico (SEMPRE obrigatório, não deve estar em examesSelecionados)
              let exameClinicoId: number | null = null
              try {
                const examesDaClinica = await clinicasService.getExamesClinicosClinica(clinicaId)
                const exameClinico = examesDaClinica.find(e => e.nome.toLowerCase().trim() === 'exame clínico')
                if (exameClinico) {
                  exameClinicoId = exameClinico.id
                }
              } catch (error) {
                console.error('❌ Erro ao buscar Exame Clínico:', error)
              }

              // Buscar obrigatórios da API baseado na variação atual
              let idsObrigatorios: number[] = []
              if (variacaoId) {
                try {
                  const examesObrigatorios = await variacoesService.getExamesClinicosPorVariacaoClinica(variacaoId, clinicaId)
                  idsObrigatorios = examesObrigatorios.map(e => e.id)
                  console.log('📋 Exames obrigatórios da variação atual:', idsObrigatorios)
                } catch (error) {
                  console.error('❌ Erro ao buscar exames obrigatórios:', error)
                }
              }

              // Separar exames do colaborador: obrigatórios vs opcionais
              // Exame Clínico SEMPRE é obrigatório, não deve estar em examesSelecionados
              const examesDoColaborador = editingEmployee.examesSelecionados
              const examesOpcionais = examesDoColaborador.filter(id => {
                // Remover obrigatórios da variação E Exame Clínico
                return !idsObrigatorios.includes(id) && id !== exameClinicoId
              })

              // Calcular contagem correta: opcionais + obrigatórios + Exame Clínico (sem duplicar)
              let countTotal = examesOpcionais.length // Opcionais

              // Adicionar Exame Clínico (sempre 1, se existir)
              if (exameClinicoId) {
                countTotal += 1
              }

              // Adicionar obrigatórios (sem duplicar Exame Clínico)
              if (idsObrigatorios.length > 0) {
                const obrigatoriosSemExameClinico = idsObrigatorios.filter(id => id !== exameClinicoId)
                countTotal += obrigatoriosSemExameClinico.length
              }

              // Atualizar editingEmployee com contagem correta
              editingEmployee.examesSelecionadosCount = countTotal

              // Armazenar apenas opcionais (sem Exame Clínico, sem obrigatórios da variação)
              setExamesSelecionados(examesOpcionais)
              console.log('📊 [loadEditingData] Contagem calculada:', {
                opcionais: examesOpcionais.length,
                obrigatorios: idsObrigatorios.length,
                exameClinico: exameClinicoId ? 1 : 0,
                total: countTotal,
                opcionaisIds: examesOpcionais,
                obrigatoriosIds: idsObrigatorios
              })
              console.log('✅ Exames opcionais do colaborador (sem obrigatórios e sem Exame Clínico):', examesOpcionais)
              console.log('📋 Exames obrigatórios (incluindo Exame Clínico) serão gerenciados automaticamente')
            } else {
              // Se não tem exames do pedido, começar vazio
              // ExamesList vai adicionar obrigatórios automaticamente (incluindo Exame Clínico)
              setExamesSelecionados([])
            }
          } catch (error) {
            console.error('❌ Erro ao carregar dados da clínica:', error)
            setError('Erro ao carregar dados da clínica e exames')
          }
        }

        setIsOpen(true)
      } else {
        // Limpar formulário quando não estiver editando
        setFormData({
          nome: '',
          nome_social: '',
          pcd: false,
          enviar_esocial: true,
          regime_contratacao: '',
          estado_civil: '',
          cpf: '',
          nascimento: '2000-01-01',
          sexo: '',
          cargo: '',
          setor: '',
          email: '',
          telefone: '',
          formacao: '',
          dataAdmissao: '',
          demissao: '',
          matricula_esocial: '',
          exame: '',
          variacao: '1',
          clinica: '',
          dataAgendamento: '',
          modeloExame: '0',
          tipoContrato: ''
        })
        setSelectedClinica(null)
        setVariacaoSelecionada(null)
        setExamesSelecionados([])
        setEditingClinica(false) // Resetar edição de clínica ao limpar formulário
        setIsCandidato(defaultIsCandidato ?? false)
        setEscolhaClinicaTipo(defaultEscolhaClinicaTipo ?? (defaultIsCandidato ? 'colaborador' : 'rh'))
        setCargoExamesAviso(null)
        if (hideAccordion || defaultOpen) {
          setIsOpen(true)
        }
      }
    }
    loadEditingData()
  }, [editingEmployee, hideAccordion, defaultOpen, defaultIsCandidato, defaultEscolhaClinicaTipo])

  // Salvar automaticamente no localStorage sempre que formData mudar (apenas quando não estiver editando)
  useEffect(() => {
    if (!editingEmployee) {
      if (formData.nome?.trim() || formData.cpf?.trim() || formData.email?.trim()) {
        localStorage.setItem('employeeFormData', JSON.stringify(formData));
      } else {
        localStorage.removeItem('employeeFormData');
      }
    }
  }, [formData, editingEmployee]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    let formattedValue = value;

    // Aplicar formatação baseada no campo
    if (name === 'cpf') {
      formattedValue = formatCPF(value);
      const cleanCpf = value.replace(/\D/g, '');
      if (cleanCpf.length === 11 && !editingEmployee && empresa?.id) {
        colaboradoresService
          .findAll({ search: cleanCpf, empresa_id: empresa.id, limit: 5, enabled: 'all' })
          .then((res) => {
            const match = (res.data || []).find(
              (c) => (c.cpf || '').replace(/\D/g, '') === cleanCpf
            );
            if (match) {
              setColaboradorExistenteDetectado(match);
            } else {
              setColaboradorExistenteDetectado(null);
            }
          })
          .catch(() => setColaboradorExistenteDetectado(null));
      } else if (cleanCpf.length < 11) {
        setColaboradorExistenteDetectado(null);
      }
    } else if (name === 'telefone') {
      formattedValue = maskPhone(value);
    }

    setFormData(prev => {
      const update: Record<string, string> = { [name]: formattedValue }

      // Se mudar o tipo de exame para algo que não seja Periódico
      // forçar o modeloExame para '0' (Presencial)
      if (name === 'exame' && formattedValue !== periodicoId) {
        update.modeloExame = '0'
      }

      return { ...prev, ...update }
    })

    // Se alterou o tipo de exame e já temos um cargo selecionado, atualiza os exames para o novo tipo
    if (name === 'exame') {
      const match = cargosList.find(c => c.nome.toLowerCase().trim() === (formData.cargo || '').toLowerCase().trim());
      if (match) {
        cargosService.getExamesByCargo(match.id, empresa?.id, loadedSetorIdRef.current || undefined, formattedValue)
          .then((res) => {
            const ids = res.exames_clinicos_ids || [];
            setExamesSelecionados(ids);
            if (ids.length > 0) {
              setCargoExamesAviso(`✨ ${ids.length} exame(s) previstos no PGR/PCMSO para o cargo "${match.nome}" foram pré-selecionados para este tipo de exame.`);
            } else {
              setCargoExamesAviso(null);
            }
          })
          .catch((err) => console.warn('Erro ao atualizar exames ao mudar tipo de exame:', err));
      }
    }
  }

  const handleAproveitarCadastroExistente = (colab: Colaborador) => {
    setExistingColaboradorId(colab.id);
    const colabIsCandidate = !colab.data_admissao || Boolean((colab as unknown as { is_candidato?: boolean }).is_candidato);
    const examePadrao = colabIsCandidate ? (admissionalId || '1') : (periodicoId || '3');
    setIsCandidato(colabIsCandidate);
    setFormData((prev) => ({
      ...prev,
      nome: colab.nome || prev.nome,
      nome_social: colab.nome_social || prev.nome_social,
      cpf: formatCPF(colab.cpf),
      nascimento: colab.data_nascimento ? colab.data_nascimento.split('T')[0] : prev.nascimento,
      sexo: colab.sexo || prev.sexo,
      email: colab.email || prev.email,
      telefone: colab.telefone ? formatTelefone(colab.telefone) : prev.telefone,
      cargo: colab.cargo?.nome || prev.cargo,
      setor: colab.setor?.nome || prev.setor,
      matricula_esocial: colab.matricula_esocial || prev.matricula_esocial,
      dataAdmissao: colab.data_admissao ? colab.data_admissao.split('T')[0] : prev.dataAdmissao,
      exame: prev.exame || examePadrao,
    }));
    if (colab.setor?.id) {
      loadedSetorIdRef.current = colab.setor.id;
    }
    if (colab.cargo?.id && colab.cargo?.nome) {
      handleCargoChange(colab.cargo.nome);
    }
    setColaboradorExistenteDetectado(null);
  };

  const handleCargoChange = async (cargoNome: string) => {
    setFormData(prev => ({ ...prev, cargo: cargoNome }))
    setCargoExamesAviso(null)

    // Se o cargo digitado/selecionado bate com algum cargo da lista de cargos cadastrados
    const match = cargosList.find(c => c.nome.toLowerCase().trim() === cargoNome.toLowerCase().trim())
    if (match) {
      setMatchedCbo(match.cbo || null)
      try {
        setLoadingExamesCargo(true)
        const res = await cargosService.getExamesByCargo(match.id, empresa?.id, loadedSetorIdRef.current || undefined, formData.exame)
        if (res.exames_clinicos_ids) {
          setExamesSelecionados(res.exames_clinicos_ids)
          if (res.exames_clinicos_ids.length > 0) {
            setCargoExamesAviso(`✨ ${res.exames_clinicos_ids.length} exame(s) previstos no PGR/PCMSO para o cargo "${match.nome}" foram pré-selecionados automaticamente.`)
          }
        }
        if (res.variacoes && res.variacoes.length > 0) {
          setFormData(prev => ({ ...prev, variacao: res.variacoes[0].id.toString() }))
          setVariacaoSelecionada(res.variacoes[0].id)
        }
      } catch (err) {
        console.warn('Erro ao carregar exames do cargo:', err)
      } finally {
        setLoadingExamesCargo(false)
      }
    } else {
      setMatchedCbo(null)
    }
  }

  const fetchCargosBySetor = useCallback(async (setorId: number, currentCargoName?: string) => {
    setLoadingCargosDoSetor(true)
    try {
      const res = await cargosService.findAll({ setor_id: setorId, empresa_id: empresa?.id, limit: 1000 })
      const cargos = res?.data || []

      // Nunca faz fallback para todos os cargos da empresa se o setor não tiver cargos
      setCargosList(cargos)

      // Se havia um cargo preenchido, verificar se ele ainda pertence ao novo setor
      if (currentCargoName) {
        const match = cargos.find(c => c.nome.toLowerCase().trim() === currentCargoName.toLowerCase().trim())
        if (!match) {
          setFormData(prev => ({ ...prev, cargo: '' }))
          setMatchedCbo(null)
          setCargoExamesAviso(null)
        } else {
          setMatchedCbo(match.cbo || null)
        }
      }
    } catch (err) {
      console.error('Erro ao carregar cargos do setor:', err)
      setCargosList([])
      if (currentCargoName) {
        setFormData(prev => ({ ...prev, cargo: '' }))
        setMatchedCbo(null)
        setCargoExamesAviso(null)
      }
    } finally {
      setLoadingCargosDoSetor(false)
    }
  }, [empresa?.id])

  // Sincronizar o setor com o carregamento de seus respectivos cargos
  useEffect(() => {
    if (formData.setor && setoresList.length > 0) {
      const matchingSetor = setoresList.find(
        s => s.nome.toLowerCase().trim() === formData.setor.toLowerCase().trim()
      )
      if (matchingSetor) {
        if (loadedSetorIdRef.current !== matchingSetor.id) {
          loadedSetorIdRef.current = matchingSetor.id
          fetchCargosBySetor(matchingSetor.id, formData.cargo)
        }
      } else {
        if (loadedSetorIdRef.current !== null) {
          loadedSetorIdRef.current = null
          setCargosList([])
          setFormData(prev => ({ ...prev, cargo: '' }))
          setMatchedCbo(null)
          setCargoExamesAviso(null)
        }
      }
    } else if (!formData.setor) {
      if (loadedSetorIdRef.current !== null) {
        loadedSetorIdRef.current = null
        setCargosList([])
        setFormData(prev => ({ ...prev, cargo: '' }))
        setMatchedCbo(null)
        setCargoExamesAviso(null)
      }
    }
  }, [formData.setor, setoresList, fetchCargosBySetor, formData.cargo])

  const handleSelectCargo = (cargo: { id: number; nome: string; cbo?: string | null }) => {
    handleCargoChange(cargo.nome)
    setShowCargoSuggestions(false)
  }

  const handleSelectSetor = (setor: { id: number; nome: string }) => {
    loadedSetorIdRef.current = setor.id
    setFormData(prev => ({ ...prev, setor: setor.nome, cargo: '' }))
    setMatchedCbo(null)
    setCargoExamesAviso(null)
    setShowSetorSuggestions(false)
    fetchCargosBySetor(setor.id)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // Validar data de admissão obrigatória
      if (!formData.dataAdmissao) {
        throw new Error('Data de admissão é estritamente obrigatória');
      }

      // Validar se clínica foi selecionada (apenas quando o RH define a clínica)
      if (escolhaClinicaTipo === 'rh' && !selectedClinica) {
        throw new Error('Por favor, selecione uma clínica')
      }

      // Validar canal de contato obrigatório quando o link for enviado ao colaborador/candidato
      if (escolhaClinicaTipo === 'colaborador') {
        const cleanPhone = formData.telefone ? formData.telefone.replace(/\D/g, '') : ''
        const cleanEmail = formData.email ? formData.email.trim() : ''
        if (cleanPhone.length < 10 && (!cleanEmail || !cleanEmail.includes('@'))) {
          throw new Error('Para enviar o link de autoagendamento ao colaborador/candidato, informe ao menos o WhatsApp (com DDD) ou o E-mail de contato.')
        }
      }

      // Gerar ID do colaborador: se for existente ou já detectado na base pelo CPF, reaproveitar o ID real
      const targetExistingId = existingColaboradorId || colaboradorExistenteDetectado?.id;
      const employeeId = editingEmployee
        ? editingEmployee.id
        : targetExistingId
          ? targetExistingId.toString()
          : `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Exames serão salvos apenas quando o pedido for criado no resumo
      // Não salvar na API aqui - apenas no localStorage
      // IMPORTANTE: Salvar apenas exames opcionais selecionados manualmente
      // Obrigatórios e Exame Clínico serão buscados automaticamente pelo backend

      console.log('📊 [handleSubmit] Salvando exames:', {
        escolhaClinicaTipo,
        opcionais: examesSelecionados.length,
        ids: examesSelecionados
      })

      // Calcular contagem total: opcionais + obrigatórios + Exame Clínico (sem duplicar)
      let countTotal = examesSelecionados.length

      if (selectedClinica) {
        // Buscar Exame Clínico (SEMPRE obrigatório)
        let exameClinicoId: number | null = null
        try {
          const examesDaClinica = await clinicasService.getExamesClinicosClinica(selectedClinica.id)
          const exameClinico = examesDaClinica.find(e => e.nome.toLowerCase().trim() === 'exame clínico')
          if (exameClinico) {
            exameClinicoId = exameClinico.id
          }
        } catch (error) {
          console.error('❌ Erro ao buscar Exame Clínico:', error)
        }

        if (exameClinicoId) {
          countTotal += 1
        }

        // Buscar obrigatórios da variação (se houver)
        let idsObrigatorios: number[] = []
        if (variacaoSelecionada) {
          try {
            const examesObrigatorios = await variacoesService.getExamesClinicosPorVariacaoClinica(variacaoSelecionada, selectedClinica.id)
            idsObrigatorios = examesObrigatorios.map(e => e.id)
            console.log('📋 Exames obrigatórios da variação:', idsObrigatorios)
          } catch (error) {
            console.error('❌ Erro ao buscar exames obrigatórios:', error)
          }
        }

        if (idsObrigatorios.length > 0) {
          const obrigatoriosSemExameClinico = idsObrigatorios.filter(id => id !== exameClinicoId)
          countTotal += obrigatoriosSemExameClinico.length
        }
      } else {
        // Quando a clínica for definida pelo colaborador via link
        countTotal = Math.max(countTotal, 1)
      }

      console.log('📊 [handleSubmit] Contagem total calculada:', {
        opcionais: examesSelecionados.length,
        total: countTotal
      })

      // Identificar cargo_id e setor_id explicitamente para não perder referência no Resumo
      const matchedCargo = cargosList.find(
        (c) => c.nome.toLowerCase().trim() === (formData.cargo || '').toLowerCase().trim()
      );
      const targetCargoId = matchedCargo?.id || editingEmployee?.cargo_id;
      const targetSetorId = loadedSetorIdRef.current || editingEmployee?.setor_id;
      const isExistente = Boolean(editingEmployee?.is_existente || targetExistingId);

      const defaultExameId = admissionalId || '1';
      const targetExame = formData.exame && formData.exame.trim() !== ''
        ? formData.exame
        : (editingEmployee?.exame || defaultExameId);

      // Criar objeto local para exibição
      const employee: Employee = {
        id: employeeId,
        ...formData,
        exame: targetExame,
        cargo_id: targetCargoId,
        setor_id: targetSetorId,
        is_existente: isExistente,
        enviar_esocial: isCandidato ? false : (formData.enviar_esocial !== false),
        cbo: matchedCbo || undefined,
        clinica: selectedClinica ? selectedClinica.id.toString() : '',
        clinicaNome: selectedClinica ? selectedClinica.nome : 'Definida pelo colaborador via link',
        dataAgendamento: selectedClinica ? formData.dataAgendamento : '',
        examesSelecionados: examesSelecionados,
        examesSelecionadosCount: countTotal,
        is_candidato: isCandidato,
        escolha_clinica_tipo: escolhaClinicaTipo,
      }

      console.log('💾 Salvando colaborador:', employee)

      // Chamar callback para adicionar/editar colaborador
      onAddEmployee(employee)

      // Limpar dados salvos e reset form
      localStorage.removeItem('employeeFormData')
      setSelectedClinica(null)
      setVariacaoSelecionada(null)
      setExamesSelecionados([])
      setIsCandidato(defaultIsCandidato ?? false)
      setEscolhaClinicaTipo(defaultEscolhaClinicaTipo ?? (defaultIsCandidato ? 'colaborador' : 'rh'))
      setCargoExamesAviso(null)
      loadedSetorIdRef.current = null
      setCargosList([])
      setFormData({
        nome: '',
        nome_social: '',
        pcd: false,
        enviar_esocial: true,
        regime_contratacao: '',
        estado_civil: '',
        cpf: '',
        nascimento: '2000-01-01',
        sexo: '',
        cargo: '',
        setor: '',
        email: '',
        telefone: '',
        formacao: '',
        dataAdmissao: '',
        demissao: '',
        matricula_esocial: '',
        exame: '',
        variacao: '1',
        clinica: '',
        dataAgendamento: '',
        modeloExame: '0',
        tipoContrato: ''
      })

      if (!hideAccordion) {
        setIsOpen(false)
      }
    } catch (error: unknown) {
      console.error('Erro ao processar colaborador:', error)
      const err = error as { message?: string }
      setError(err?.message || 'Erro ao processar colaborador')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={hideAccordion ? 'w-full' : 'bg-white rounded-lg shadow-sm'}>
      {!hideAccordion && (
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
          <div className="flex items-center justify-between w-full">
            <button
              ref={accordionHeaderRef}
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center text-white hover:text-green-100 transition-colors flex-1 text-left"
            >
              <svg className={`w-5 h-5 mr-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              <div>
                <h3 className="font-semibold text-lg">
                  {editingEmployee ? 'Editar Colaborador' : (hasEmployees ? 'Incluir mais colaboradores' : 'Adicionar Colaborador')}
                </h3>
                <p className="text-green-100 text-sm">
                  {editingEmployee
                    ? 'Atualize as informações do colaborador'
                    : 'Preencha os dados do novo colaborador'
                  }
                </p>
              </div>
            </button>
            <div className="flex items-center gap-2 ml-4">
              {editingEmployee && onCancelEdit && (
                <button
                  onClick={() => {
                    onCancelEdit()
                    setIsOpen(false)
                  }}
                  className="bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-lg transition-colors text-sm"
                >
                  Cancelar
                </button>
              )}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isOpen ? 'bg-white text-green-600' : 'bg-green-500 text-white'
                }`}>
                {isOpen ? '−' : '+'}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={hideAccordion ? 'pt-2' : 'p-6'}>
        {hideAccordion && editingEmployee && onCancelEdit && (
          <div className="mb-4 flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-xl">
            <span className="text-xs font-bold text-blue-900">
              Modo de Edição Ativo: Editando colaborador <strong>{editingEmployee.nome}</strong>
            </span>
            <button
              onClick={onCancelEdit}
              className="px-3 py-1 text-xs font-semibold text-blue-700 bg-white hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
            >
              Cancelar Edição
            </button>
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
            {error}
          </div>
        )}

        {(isOpen || hideAccordion) && (
          <>
            {/* Busca de Colaborador */}
            {!editingEmployee && !hideAccordion && (
              <div className="mb-8 border-b border-gray-100 pb-8">
                <CollaboratorSearch
                  onSelect={(colaborador) => {
                    console.log('👤 Colaborador selecionado:', colaborador)

                    // Mapear dados do colaborador para o formulário
                    setFormData(prev => ({
                      ...prev,
                      nome: colaborador.nome,
                      cpf: formatCPF(colaborador.cpf),
                      nascimento: colaborador.data_nascimento ? colaborador.data_nascimento.split('T')[0] : '2000-01-01',
                      sexo: colaborador.sexo || '',
                      cargo: colaborador.cargo?.nome || '',
                      setor: colaborador.setor?.nome || '',
                      email: colaborador.email || '',
                      telefone: colaborador.telefone ? maskPhone(colaborador.telefone) : '',
                      dataAdmissao: colaborador.data_admissao ? colaborador.data_admissao.split('T')[0] : '',
                      demissao: colaborador.data_demissao ? colaborador.data_demissao.split('T')[0] : '',
                      tipoContrato: colaborador.tipo_id ? colaborador.tipo_id.toString() : '',
                      // Manter campos de agendamento limpos ou preservar se já preenchidos?
                      // Por enquanto, apenas dados pessoais e funcionais
                    }))

                    // Tentar selecionar formação se houver match
                    if (colaborador.formacao_academica_id) {
                      setFormData(prev => ({ ...prev, formacao: colaborador.formacao_academica_id?.toString() || '' }))
                    }

                    // Vincular setor do colaborador
                    const colabSetorId = colaborador.setor?.id || (colaborador as { setor_id?: number }).setor_id;
                    if (colabSetorId) {
                      loadedSetorIdRef.current = Number(colabSetorId);
                      fetchCargosBySetor(Number(colabSetorId), colaborador.cargo?.nome);
                    }

                    // Se colaborador já possui cargo cadastrado, buscar exames previstos no PGR/PCMSO automaticamente
                    if (colaborador.cargo?.nome) {
                      handleCargoChange(colaborador.cargo.nome)
                    }
                  }}
                />
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Modalidade de Cadastro: Candidato vs Colaborador */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-6">
                <label className="block text-sm font-semibold text-slate-800 mb-3">
                  Modalidade do Colaborador *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCandidato(false)
                    }}
                    className={`flex items-start p-4 rounded-xl border-2 text-left transition-all ${
                      !isCandidato
                        ? 'border-green-600 bg-green-50/60 shadow-xs'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 mt-0.5 shrink-0 ${
                      !isCandidato ? 'border-green-600 bg-green-600' : 'border-slate-300'
                    }`}>
                      {!isCandidato && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <div>
                      <span className="text-sm font-bold text-slate-900 block">Colaborador Contratado / Admissão Aprovada</span>
                      <span className="text-xs text-slate-500 mt-0.5 block">
                        Funcionário ativo ou candidato já aprovado para contratação (Admissional, Periódico, Demissional, Retorno, Mudança de Risco). Fluxo direto sem retenção de eSocial.
                      </span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setIsCandidato(true)
                      const admissional = tiposExame.find(t => t.nome.toLowerCase().includes('admissional'))
                      if (admissional) {
                        setFormData(prev => ({ ...prev, exame: admissional.id.toString(), modeloExame: '0' }))
                      }
                    }}
                    className={`flex items-start p-4 rounded-xl border-2 text-left transition-all ${
                      isCandidato
                        ? 'border-green-600 bg-green-50/60 shadow-xs'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 mt-0.5 shrink-0 ${
                      isCandidato ? 'border-green-600 bg-green-600' : 'border-slate-300'
                    }`}>
                      {isCandidato && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <div>
                      <span className="text-sm font-bold text-slate-900 flex items-center gap-2">
                        Candidato em Processo Seletivo
                        <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-200">
                          Retenção eSocial
                        </span>
                      </span>
                      <span className="text-xs text-slate-500 mt-0.5 block">
                        Exame Admissional com aprovação posterior. O evento S-2220 só é enviado ao eSocial após você aprovar a contratação em "Aprovar Candidatos".
                      </span>
                    </div>
                  </button>
                </div>

                {isCandidato && (
                  <div className="mt-4 p-3.5 bg-amber-50/90 border border-amber-200 rounded-lg text-xs text-amber-900 flex items-start gap-2.5">
                    <span className="text-base leading-none">🛡️</span>
                    <div>
                      <strong>Proteção e Retenção Trabalhista:</strong> O ASO será emitido pela clínica credenciada e ficará disponível para download em <strong>"Aprovar Candidatos"</strong> no painel. O evento eSocial S-2220 permanecerá bloqueado até sua aprovação formal da contratação.
                    </div>
                  </div>
                )}
              </div>

              {/* Dados pessoais */}
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Dados Pessoais</h4>
                </div>

                {/* Linha 1: Nome Completo e Nome Social */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo <span className="text-red-500 font-bold">*</span>
                    </label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Social <span className="text-gray-400 font-normal">(Opcional)</span>
                    </label>
                    <input
                      type="text"
                      name="nome_social"
                      value={formData.nome_social || ''}
                      onChange={handleInputChange}
                      placeholder="Nome social para documentos oficiais"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                    />
                  </div>
                </div>

                {/* Linha 1.5: CPF, Data Nascimento, Sexo, Estado Civil */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CPF <span className="text-red-500 font-bold">*</span>
                    </label>
                    <input
                      type="text"
                      name="cpf"
                      value={formData.cpf}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                      placeholder="000.000.000-00"
                      required
                    />
                    {existingColaboradorId && (
                      <div className="mt-1 flex items-center gap-1 text-[11px] text-emerald-700 font-medium">
                        <span>✓ Vinculado ao cadastro #{existingColaboradorId} da empresa.</span>
                        <button
                          type="button"
                          onClick={() => setExistingColaboradorId(null)}
                          className="text-gray-400 hover:text-gray-600 underline text-[10px] ml-1 cursor-pointer"
                        >
                          (desvincular)
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data de Nascimento <span className="text-red-500 font-bold">*</span>
                    </label>
                    <input
                      type="date"
                      name="nascimento"
                      value={formData.nascimento}
                      onChange={handleInputChange}
                      max={new Date(new Date().setFullYear(new Date().getFullYear() - 14)).toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sexo Biológico <span className="text-red-500 font-bold">*</span>
                    </label>
                    <select
                      name="sexo"
                      value={formData.sexo}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                      required
                    >
                      <option value="">Selecione</option>
                      <option value="M">Masculino</option>
                      <option value="F">Feminino</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estado Civil
                    </label>
                    <select
                      name="estado_civil"
                      value={formData.estado_civil || ''}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                    >
                      <option value="">Selecione</option>
                      <option value="Solteiro(a)">Solteiro(a)</option>
                      <option value="Casado(a)">Casado(a)</option>
                      <option value="União Estável">União Estável</option>
                      <option value="Divorciado(a)">Divorciado(a)</option>
                      <option value="Viúvo(a)">Viúvo(a)</option>
                    </select>
                  </div>

                  {colaboradorExistenteDetectado && (
                    <div className="col-span-1 md:col-span-4 p-4 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-xs">
                      <div className="space-y-1">
                        <div className="font-bold flex flex-wrap items-center gap-2 text-sm">
                          <span>⚠️ Cadastro existente encontrado na empresa:</span>
                          <span className="text-gray-900 font-semibold">{colaboradorExistenteDetectado.nome}</span>
                          {!colaboradorExistenteDetectado.enabled && (
                            <span className="bg-amber-200 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">
                              Demitido / Inativo
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-amber-800 leading-relaxed">
                          {colaboradorExistenteDetectado.data_admissao
                            ? `Admissão anterior: ${colaboradorExistenteDetectado.data_admissao.split('T')[0]}`
                            : 'Candidato com agendamento pendente/cancelado'}
                          {colaboradorExistenteDetectado.data_demissao
                            ? ` • Demissão: ${colaboradorExistenteDetectado.data_demissao.split('T')[0]}`
                            : ''}
                          {colaboradorExistenteDetectado.cargo?.nome ? ` • Cargo: ${colaboradorExistenteDetectado.cargo.nome}` : ''}
                          . {!colaboradorExistenteDetectado.enabled
                            ? 'Recontratação permitida: aproveite este cadastro para reativar o colaborador com o novo agendamento.'
                            : 'Para evitar conflito de períodos de admissão, aproveite este cadastro para o agendamento.'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleAproveitarCadastroExistente(colaboradorExistenteDetectado)}
                        className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold shrink-0 cursor-pointer shadow-xs transition-colors text-center text-sm"
                      >
                        Aproveitar Cadastro
                      </button>
                    </div>
                  )}
                </div>

                {/* Linha 2: Tipo Contrato, Regime de Contratação, Formação, Data Admissão */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Contrato <span className="text-red-500 font-bold">*</span>
                    </label>
                    <select
                      name="tipoContrato"
                      value={formData.tipoContrato}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                      required
                    >
                      <option value="">Selecione</option>
                      {contractTypes.map(type => (
                        <option key={type.id} value={type.id}>{type.descricao}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Regime de Contratação
                    </label>
                    <select
                      name="regime_contratacao"
                      value={formData.regime_contratacao || ''}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                    >
                      <option value="">Selecione</option>
                      <option value="CLT">CLT</option>
                      <option value="PJ">PJ</option>
                      <option value="Estágio">Estágio</option>
                      <option value="Temporário">Temporário</option>
                      <option value="Autônomo">Autônomo</option>
                      <option value="Jovem Aprendiz">Jovem Aprendiz</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Formação Acadêmica
                    </label>
                    <select
                      name="formacao"
                      value={formData.formacao}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                    >
                      <option value="">Selecione (opcional)</option>
                      {formacoes.map(formacao => (
                        <option key={formacao.id} value={formacao.id}>{formacao.descricao}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data de Admissão {isCandidato ? '(Previsão)' : ''} <span className="text-red-500 font-bold">*</span>
                    </label>
                    <input
                      type="date"
                      name="dataAdmissao"
                      value={formData.dataAdmissao}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                      required
                    />
                  </div>
                </div>

                {/* Flags PCD e eSocial */}
                <div className="flex flex-wrap gap-6 items-center mt-6 pt-4 border-t border-gray-200">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      name="pcd"
                      checked={formData.pcd === true}
                      onChange={(e) => setFormData(prev => ({ ...prev, pcd: e.target.checked }))}
                      className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">PCD (Pessoa com Deficiência)</span>
                  </label>

                  <label
                    className={`flex items-center gap-2 select-none ${isCandidato ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
                    title={isCandidato ? 'Candidatos em processo admissional não transmitem ao e-Social até a aprovação formal da contratação.' : undefined}
                  >
                    <input
                      type="checkbox"
                      name="enviar_esocial"
                      checked={isCandidato ? false : formData.enviar_esocial !== false}
                      disabled={isCandidato}
                      onChange={(e) => setFormData(prev => ({ ...prev, enviar_esocial: e.target.checked }))}
                      className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500 disabled:opacity-50"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Enviar para o e-Social
                      {isCandidato && (
                        <span className="ml-1 text-xs text-amber-600 font-normal">
                          (Fixo como Não para candidatos até aprovação)
                        </span>
                      )}
                    </span>
                  </label>
                </div>

                {/* Linha 3: Setor e Cargo com Autocomplete e Modal de Busca */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  {/* Setor */}
                  <div className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Setor <span className="text-red-500">*</span>
                      </label>
                    </div>

                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type="text"
                          name="setor"
                          value={formData.setor}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, setor: e.target.value }))
                            setShowSetorSuggestions(true)
                          }}
                          onFocus={() => setShowSetorSuggestions(true)}
                          onBlur={() => {
                            setTimeout(() => setShowSetorSuggestions(false), 200)
                          }}
                          placeholder="Digite para buscar ou selecione..."
                          autoComplete="off"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                          required
                        />
                        {/* Dropdown de sugestões em tempo real */}
                        {showSetorSuggestions && formData.setor && (
                          <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto divide-y divide-gray-100">
                            {setoresList
                              .filter(s => s.nome.toLowerCase().includes(formData.setor.toLowerCase()))
                              .slice(0, 10)
                              .map(s => (
                                <button
                                  key={s.id}
                                  type="button"
                                  onMouseDown={() => handleSelectSetor(s)}
                                  className="w-full text-left px-3.5 py-2.5 text-xs hover:bg-green-50 font-medium text-gray-800"
                                >
                                  {s.nome}
                                </button>
                              ))}
                            {setoresList.filter(s => s.nome.toLowerCase().includes(formData.setor.toLowerCase())).length === 0 && (
                              <div className="p-3 text-xs text-gray-500 text-center">
                                Setor novo. Será cadastrado automaticamente.
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setIsSetorModalOpen(true);
                          if (setoresList.length === 0 && empresa?.id) {
                            setoresService.findAll({ empresa_id: empresa.id, limit: 1000 }).then((res) => {
                              if (res?.data) setSetoresList(res.data);
                            }).catch(() => {});
                          }
                        }}
                        className="px-3.5 py-3 bg-gray-50 border border-gray-300 rounded-lg hover:bg-green-50 hover:border-green-400 text-gray-700 hover:text-green-700 transition-colors flex items-center gap-1.5 shrink-0"
                        title="Buscar Setor na Empresa"
                      >
                        <MagnifyingGlassIcon className="w-5 h-5 text-gray-500" />
                        <span className="hidden sm:inline text-xs font-semibold">Buscar</span>
                      </button>
                    </div>
                  </div>

                  {/* Cargo */}
                  <div className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Cargo <span className="text-red-500">*</span>
                      </label>
                      {loadingCargosDoSetor && (
                        <span className="text-xs text-green-600 flex items-center gap-1 font-medium">
                          <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Carregando cargos do setor...
                        </span>
                      )}
                      {loadingExamesCargo && (
                        <span className="text-xs text-green-600 flex items-center gap-1 font-medium">
                          <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Buscando exames do cargo...
                        </span>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type="text"
                          name="cargo"
                          value={formData.cargo}
                          onChange={(e) => {
                            handleCargoChange(e.target.value)
                            setShowCargoSuggestions(true)
                          }}
                          onFocus={() => {
                            if (formData.setor?.trim()) {
                              setShowCargoSuggestions(true)
                            }
                          }}
                          onBlur={() => {
                            // Pequeno delay para permitir clique nas sugestões
                            setTimeout(() => setShowCargoSuggestions(false), 200)
                          }}
                          placeholder={
                            !formData.setor?.trim()
                              ? 'Selecione o setor primeiro...'
                              : loadingCargosDoSetor
                              ? 'Carregando cargos do setor...'
                              : cargosList.length === 0
                              ? 'Nenhum cargo vinculado a este setor'
                              : 'Digite para buscar ou selecione...'
                          }
                          autoComplete="off"
                          disabled={!formData.setor?.trim() || loadingCargosDoSetor}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                          required
                        />
                        {/* Dropdown de sugestões em tempo real */}
                        {showCargoSuggestions && formData.setor?.trim() && !loadingCargosDoSetor && (
                          <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto divide-y divide-gray-100">
                            {cargosList
                              .filter(c => !formData.cargo || c.nome.toLowerCase().includes(formData.cargo.toLowerCase()))
                              .slice(0, 10)
                              .map(c => (
                                <button
                                  key={c.id}
                                  type="button"
                                  onMouseDown={() => handleSelectCargo(c)}
                                  className="w-full text-left px-3.5 py-2.5 text-xs hover:bg-green-50 flex items-center justify-between"
                                >
                                  <span className="font-medium text-gray-800">{c.nome}</span>
                                  {c.cbo && (
                                    <span className="text-[10px] text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                                      CBO: {c.cbo}
                                    </span>
                                  )}
                                </button>
                              ))}
                            {cargosList.length === 0 && (
                              <div className="p-3 text-xs text-amber-700 bg-amber-50 text-center font-medium">
                                Nenhum cargo vinculado a este setor.
                              </div>
                            )}
                            {cargosList.length > 0 && formData.cargo && cargosList.filter(c => c.nome.toLowerCase().includes(formData.cargo.toLowerCase())).length === 0 && (
                              <div className="p-3 text-xs text-gray-500 text-center">
                                Nenhum cargo vinculado corresponde à busca.
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          if (formData.setor?.trim()) {
                            setIsCargoModalOpen(true)
                          }
                        }}
                        disabled={!formData.setor?.trim() || loadingCargosDoSetor}
                        className="px-3.5 py-3 bg-gray-50 border border-gray-300 rounded-lg hover:bg-green-50 hover:border-green-400 text-gray-700 hover:text-green-700 transition-colors flex items-center gap-1.5 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-50 disabled:hover:text-gray-700"
                        title={!formData.setor?.trim() ? 'Selecione um setor antes de buscar o cargo' : 'Buscar Cargo no Setor'}
                      >
                        <MagnifyingGlassIcon className="w-5 h-5 text-gray-500" />
                        <span className="hidden sm:inline text-xs font-semibold">Buscar</span>
                      </button>
                    </div>

                    {!formData.setor?.trim() && (
                      <p className="mt-1.5 text-xs text-amber-600 font-medium">
                        ⚠️ Selecione o setor primeiro para listar os cargos vinculados.
                      </p>
                    )}
                    {matchedCbo && (
                      <p className="mt-1.5 text-xs text-gray-600 font-medium flex items-center gap-1">
                        <span className="font-semibold text-gray-700">CBO Vinculado:</span>
                        <span className="px-2 py-0.5 bg-gray-100 border border-gray-200 rounded text-gray-800 font-mono">{matchedCbo}</span>
                      </p>
                    )}
                    {cargoExamesAviso && (
                      <p className="mt-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2">
                        {cargoExamesAviso}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contato */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                    required
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Telefone / WhatsApp (Celular)
                    </label>
                    {escolhaClinicaTipo === 'colaborador' && !formData.telefone && (
                      <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                        Recomendado para envio via WhatsApp
                      </span>
                    )}
                  </div>
                  <input
                    type="tel"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900 font-mono"
                    placeholder="(00) 00000-0000"
                    maxLength={15}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Utilizado para envio do convite e link de escolha de clínica via WhatsApp.
                  </p>
                </div>
              </div>

              {/* Dados do exame */}
              <div className="border-t border-gray-200 pt-8">
                <div className="flex items-center mb-6">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm3 2a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Dados do Exame</h4>
                </div>

                <div className="bg-emerald-50/80 border border-emerald-200 rounded-2xl p-5 mb-6 shadow-xs">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-black text-xs shadow-xs">
                        ASO
                      </div>
                      <div>
                        <span className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded">
                          Tipo de Exame
                        </span>
                        <h4 className="text-base font-extrabold text-emerald-950 mt-0.5">
                          Exame Admissional
                        </h4>
                      </div>
                    </div>
                    <span className="self-start sm:self-auto text-[11px] font-bold text-emerald-800 bg-white px-3 py-1 rounded-full border border-emerald-300 shadow-2xs">
                      Padrão para Novo Colaborador
                    </span>
                  </div>
                  <p className="text-xs text-emerald-800/90 mt-3 leading-relaxed">
                    O cadastro de novos colaboradores e candidatos destina-se exclusivamente à realização do <strong>Exame Admissional</strong>. Para agendar exames periódicos, demissionais, retorno ou mudança de função, utilize a busca de colaboradores já cadastrados na base da empresa.
                  </p>
                </div>

                {/* Quem definirá a clínica de atendimento: RH agora vs Link ao Colaborador */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mt-6 mb-6">
                  <label className="block text-sm font-semibold text-slate-800 mb-3">
                    Quem definirá a clínica de atendimento? *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setEscolhaClinicaTipo('rh')}
                      className={`flex items-start p-4 rounded-xl border-2 text-left transition-all ${
                        escolhaClinicaTipo === 'rh'
                          ? 'border-green-600 bg-green-50/60 shadow-xs'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 mt-0.5 shrink-0 ${
                        escolhaClinicaTipo === 'rh' ? 'border-green-600 bg-green-600' : 'border-slate-300'
                      }`}>
                        {escolhaClinicaTipo === 'rh' && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>
                      <div>
                        <span className="text-sm font-bold text-slate-900 block">RH seleciona agora</span>
                        <span className="text-xs text-slate-500 mt-0.5 block">
                          Você pesquisa a rede credenciada e define a clínica e a data do exame imediatamente
                        </span>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setEscolhaClinicaTipo('colaborador')
                        setSelectedClinica(null)
                        setFormData(prev => ({ ...prev, clinica: '', dataAgendamento: '' }))
                      }}
                      className={`flex items-start p-4 rounded-xl border-2 text-left transition-all ${
                        escolhaClinicaTipo === 'colaborador'
                          ? 'border-green-600 bg-green-50/60 shadow-xs'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 mt-0.5 shrink-0 ${
                        escolhaClinicaTipo === 'colaborador' ? 'border-green-600 bg-green-600' : 'border-slate-300'
                      }`}>
                        {escolhaClinicaTipo === 'colaborador' && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>
                      <div>
                        <span className="text-sm font-bold text-slate-900 flex items-center gap-2">
                          Enviar link ao colaborador
                          <span className="bg-blue-100 text-blue-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-blue-200">
                            Geolocalização
                          </span>
                        </span>
                        <span className="text-xs text-slate-500 mt-0.5 block">
                          O candidato/colaborador escolhe a clínica mais próxima da residência dele
                        </span>
                      </div>
                    </button>
                  </div>

                  {escolhaClinicaTipo === 'colaborador' && (
                    <div className="mt-4 space-y-2.5">
                      <div className="p-3.5 bg-blue-50/90 border border-blue-200 rounded-xl text-xs text-blue-900 flex items-start gap-2.5">
                        <span className="text-base leading-none">📲</span>
                        <div>
                          <strong>Link de Agendamento Dinâmico:</strong> O colaborador receberá o link seguro para escolher a credenciada mais conveniente com base na sua localização.
                        </div>
                      </div>

                      {/* Avisozinho de Canais de Envio e Atalho Fácil */}
                      {(() => {
                        const cleanPhone = formData.telefone ? String(formData.telefone).replace(/\D/g, '') : '';
                        const cleanEmail = formData.email ? String(formData.email).trim() : '';
                        const hasPhone = cleanPhone.length >= 10;
                        const hasEmail = cleanEmail.length > 3 && cleanEmail.includes('@');

                        if (hasPhone && hasEmail) {
                          return (
                            <div className="p-3 bg-emerald-50 border border-emerald-300 rounded-xl text-xs text-emerald-900 flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" />
                              <span>Tudo certo! O link será enviado por <strong>WhatsApp</strong> e <strong>E-mail</strong>.</span>
                            </div>
                          );
                        }

                        if (!hasPhone && hasEmail) {
                          return (
                            <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-950 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 shadow-2xs">
                              <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-amber-500 shrink-0" />
                                <span>
                                  <strong>Atenção:</strong> Sem celular cadastrado. O link de agendamento será enviado <strong>apenas por E-mail</strong>.
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const telInput = document.querySelector<HTMLInputElement>('input[name="telefone"]');
                                  if (telInput) {
                                    telInput.focus();
                                    telInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  }
                                }}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-amber-900 bg-amber-200/80 hover:bg-amber-300 border border-amber-400/60 rounded-lg transition-colors cursor-pointer self-start sm:self-auto shrink-0"
                              >
                                <span>+ Preencher Celular / WhatsApp</span>
                              </button>
                            </div>
                          );
                        }

                        if (hasPhone && !hasEmail) {
                          return (
                            <div className="p-3 bg-sky-50 border border-sky-300 rounded-xl text-xs text-sky-950 flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full bg-sky-500 shrink-0" />
                              <span>O link de agendamento será enviado <strong>apenas por WhatsApp</strong>.</span>
                            </div>
                          );
                        }

                        return (
                          <div className="p-3 bg-red-50 border border-red-300 rounded-xl text-xs text-red-900 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" />
                              <span><strong>Atenção:</strong> Nenhum canal de contato informado! Preencha ao menos o E-mail ou WhatsApp para envio do link.</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const telInput = document.querySelector<HTMLInputElement>('input[name="telefone"]');
                                if (telInput) {
                                  telInput.focus();
                                  telInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }
                              }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-red-900 bg-red-200 hover:bg-red-300 border border-red-400 rounded-lg transition-colors cursor-pointer self-start sm:self-auto shrink-0"
                            >
                              <span>+ Preencher Contato</span>
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {escolhaClinicaTipo === 'rh' ? (
                  <>
                    {/* Busca de Clínica */}
                    <div className="mt-6">
                      {editingEmployee && selectedClinica && !editingClinica ? (
                        // Modo edição: mostrar apenas card da clínica selecionada com botão editar
                        <div className="relative p-4 rounded-lg border-2 border-green-500 bg-green-50 shadow-md">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-bold text-lg text-green-900">
                                  {selectedClinica.nome}
                                </h4>
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full font-medium">
                                  Selecionada
                                </span>
                              </div>

                              <div className="space-y-1 text-sm text-gray-600">
                                <p className="flex items-start gap-2">
                                  <svg className="w-4 h-4 mt-0.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  <span>
                                    {selectedClinica.endereco}, {selectedClinica.numero}
                                    {selectedClinica.complemento && ` - ${selectedClinica.complemento}`}
                                    <br />
                                    {selectedClinica.bairro}, {selectedClinica.cidade} - {selectedClinica.uf}
                                  </span>
                                </p>

                                {selectedClinica.telefones && selectedClinica.telefones.length > 0 && (
                                  <p className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    {selectedClinica.telefones[0]}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-3 ml-4">
                              <div className="w-6 h-6 rounded-full border-2 bg-green-500 border-green-500 text-white flex items-center justify-center flex-shrink-0">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <button
                                onClick={() => setEditingClinica(true)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Editar
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // Modo normal ou editando clínica: mostrar busca completa
                        <ClinicaSearch
                          onClinicaSelect={async (clinica) => {
                            console.log('🏥 EmployeeForm: Clínica selecionada:', clinica.id, clinica.nome)
                            setSelectedClinica(clinica)
                            setEditingClinica(false) // Fechar modo edição quando selecionar nova clínica
                            setFormData(prev => ({ ...prev, clinica: clinica.id.toString() }))
                          }}
                          selectedClinica={selectedClinica}
                          modeloExame={formData.modeloExame}
                        />
                      )}
                    </div>

                    {/* Data de Agendamento */}
                    {datasDisponiveis.length > 0 && (
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Data de Agendamento *
                        </label>
                        <select
                          name="dataAgendamento"
                          value={formData.dataAgendamento}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
                          required
                        >
                          <option value="">Selecione uma data</option>
                          {datasDisponiveis.map((data) => (
                            <option key={data} value={data}>
                              {(() => {
                                // Se já estiver no formato brasileiro, usar diretamente
                                if (data.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                                  const [dia, mes, ano] = data.split('/')
                                  const dataObj = new Date(`${ano}-${mes}-${dia}T00:00:00`)
                                  return dataObj.toLocaleDateString('pt-BR', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })
                                }
                                // Fallback para outros formatos
                                try {
                                  return new Date(data).toLocaleDateString('pt-BR', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })
                                } catch {
                                  return data
                                }
                              })()}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Próximas {datasDisponiveis.length} datas disponíveis para agendamento
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="mt-6 p-4 bg-emerald-50/70 border border-emerald-200 rounded-xl text-xs text-emerald-950 flex items-start gap-3">
                    <span className="text-xl">📍</span>
                    <div>
                      <span className="font-bold text-sm block text-emerald-900 mb-0.5">Clínica e data serão definidas pelo colaborador</span>
                      Não é necessário selecionar clínica agora. Os exames configurados neste agendamento serão automaticamente direcionados à clínica credenciada que o colaborador escolher pelo link dinâmico.
                    </div>
                  </div>
                )}

                {/* Exames do PGR/PCMSO vinculados ao Cargo */}
                <div className="mt-6 p-4 bg-emerald-50/50 border border-emerald-200 rounded-xl">
                  <h5 className="text-sm font-bold text-emerald-950 mb-1 flex items-center gap-1.5">
                    <span>📋</span> Exames Vinculados conforme PGR / PCMSO
                  </h5>
                  <p className="text-xs text-emerald-700 leading-relaxed">
                    {examesSelecionados.length > 0
                      ? `Os exames necessários são vinculados automaticamente de acordo com o risco e o cargo parametrizado. ${examesSelecionados.length} exame(s) previstos no PGR/PCMSO foram incluídos além do Exame Clínico obrigatório.`
                      : 'O Exame Clínico Ocupacional obrigatório está incluído. Se o cargo possuir exames complementares cadastrados no PGR/PCMSO, eles serão anexados automaticamente.'}
                  </p>
                  {cargoExamesAviso && (
                    <div className="mt-2.5">
                      <span className="inline-block text-xs font-semibold bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-md border border-emerald-300">
                        {cargoExamesAviso}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setEditingClinica(false);
                    localStorage.removeItem('employeeFormData');
                    setFormData({
                      nome: '',
                      nome_social: '',
                      pcd: false,
                      enviar_esocial: true,
                      regime_contratacao: '',
                      estado_civil: '',
                      cpf: '',
                      nascimento: '2000-01-01',
                      sexo: '',
                      cargo: '',
                      setor: '',
                      email: '',
                      telefone: '',
                      formacao: '',
                      dataAdmissao: '',
                      matricula_esocial: '',
                      demissao: '',
                      exame: '',
                      variacao: '',
                      clinica: '',
                      tipoContrato: '',
                      modeloExame: '0',
                      dataAgendamento: '',
                    });
                    setSelectedClinica(null);
                    setVariacaoSelecionada(null);
                    setExamesSelecionados([]);
                    setIsCandidato(false);
                    setEscolhaClinicaTipo('rh');
                    setCargoExamesAviso(null);
                    loadedSetorIdRef.current = null;
                    setCargosList([]);
                    if (onCancelEdit) {
                      onCancelEdit();
                    }
                    if (!hideAccordion) {
                      setIsOpen(false);
                    }
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors font-medium"
                >
                  {editingEmployee ? 'Cancelar Edição' : 'Limpar e Cancelar'}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-6 py-3 rounded-lg transition-colors disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium shadow-sm hover:shadow-md"
                >
                  {loading && (
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {editingEmployee ? 'Atualizar Colaborador' : 'Adicionar Colaborador'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>

      {/* Modal de Busca de Cargos */}
      <SearchLookupModal
        isOpen={isCargoModalOpen}
        onClose={() => setIsCargoModalOpen(false)}
        title={formData.setor ? `Cargos do Setor: ${formData.setor}` : "Selecionar Cargo da Empresa"}
        subtitle={formData.setor ? `Cargos vinculados ao setor "${formData.setor}" com riscos e exames do PGR/PCMSO` : "Cargos cadastrados para a empresa com riscos e exames do PGR/PCMSO"}
        placeholder="Buscar cargo por nome ou CBO..."
        selectedName={formData.cargo}
        fetchData={async (params) => {
          if (!loadedSetorIdRef.current) {
            return {
              data: [],
              meta: { total: 0, page: 1, limit: params.limit, totalPages: 0 },
            };
          }
          const res = await cargosService.findAll({
            empresa_id: empresa?.id,
            setor_id: loadedSetorIdRef.current,
            limit: params.limit,
            page: params.page,
            search: params.search,
          });
          return res;
        }}
        staticItems={cargosList}
        onSelect={(item) => handleSelectCargo(item)}
      />

      {/* Modal de Busca de Setores */}
      <SearchLookupModal
        isOpen={isSetorModalOpen}
        onClose={() => setIsSetorModalOpen(false)}
        title="Selecionar Setor da Empresa"
        subtitle="Setores cadastrados para a empresa logada"
        placeholder="Buscar setor por nome..."
        selectedName={formData.setor}
        fetchData={async (params) => {
          const res = await setoresService.findAll({
            empresa_id: empresa?.id,
            limit: params.limit,
            page: params.page,
            search: params.search,
          });
          return res;
        }}
        staticItems={setoresList}
        onSelect={(item) => handleSelectSetor(item)}
      />
    </div>
  )
}

